'use client';

import React, { useEffect, useRef, useState } from 'react';

interface FormState {
  name: string;
  email: string;
  phone: string;
  message: string;
}

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [formState, setFormState] = useState<FormState>({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-reveal').forEach((el) => el.classList.add('visible'));
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formState),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to send your message. Please try again.');
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('An unexpected error occurred. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="py-20 lg:py-28 bg-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 space-y-3">
          <div className="section-reveal stagger-1">
            <span className="section-label">Contact</span>
          </div>
          <h2 className="section-reveal stagger-2 font-display text-foreground leading-tight" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)' }}>
            Get in Touch
          </h2>
          <p className="section-reveal stagger-3 text-muted-foreground max-w-lg mx-auto">
            Reach out to EWIN Lifescience for product enquiries or partnership discussions.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="lg:col-span-5 space-y-2 section-reveal stagger-2">
            <div className="contact-info-item">
              <div className="w-10 h-10 bg-blue-50 text-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M3 5C3 3.9 3.9 3 5 3H7.28C7.67 3 8 3.27 8.1 3.65L9.07 7.35C9.18 7.77 8.97 8.21 8.57 8.4L7.12 9.08C7.93 10.72 9.28 12.07 10.92 12.88L11.6 11.43C11.79 11.03 12.23 10.82 12.65 10.93L16.35 11.9C16.73 12 17 12.33 17 12.72V15C17 16.1 16.1 17 15 17C8.37 17 3 11.63 3 5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-0.5">Phone</p>
                <a href="tel:+916376769003" className="text-foreground font-semibold hover:text-primary transition-colors">
                  +91 6376 769 003
                </a>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="w-10 h-10 bg-blue-50 text-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M3 6L10 11L17 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="3" y="5" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-0.5">Email</p>
                <a href="mailto:ewinlifescience2005@gmail.com" className="text-foreground font-semibold hover:text-primary transition-colors break-all">
                  ewinlifescience2005@gmail.com
                </a>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="w-10 h-10 bg-blue-50 text-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <rect x="3" y="3" width="14" height="14" rx="4" stroke="currentColor" strokeWidth="1.5"/>
                  <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.5"/>
                  <circle cx="14.5" cy="5.5" r="1" fill="currentColor"/>
                </svg>
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-0.5">Instagram</p>
                <a
                  href="https://www.instagram.com/ewinlifesciences2005"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground font-semibold hover:text-primary transition-colors"
                >
                  @ewinlifesciences2005
                </a>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="w-10 h-10 bg-blue-50 text-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M10 2C7.2 2 5 4.2 5 7C5 11 10 18 10 18C10 18 15 11 15 7C15 4.2 12.8 2 10 2Z" stroke="currentColor" strokeWidth="1.5"/>
                  <circle cx="10" cy="7" r="2" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">Office Address</p>
                <address className="not-italic text-foreground font-medium text-sm leading-relaxed">
                  Khasra No., Kunda Ki Dhani,<br />
                  Sirsi, Gandhi Path,<br />
                  Jaipur, Rajasthan 302034
                </address>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7 section-reveal stagger-3">
            <div className="bg-background rounded-2xl border border-border p-8">
              {submitted ? (
                <div className="text-center py-12 space-y-4">
                  <div className="w-16 h-16 bg-green-50 text-secondary rounded-full flex items-center justify-center mx-auto">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                      <path d="M6 16L12 22L26 8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="font-display text-xl text-foreground">Thank You for Your Inquiry!</h3>
                  <p className="text-muted-foreground">Thank you for your inquiry. We will get back to you shortly.</p>
                  <button
                    onClick={() => { setSubmitted(false); setFormState({ name: '', email: '', phone: '', message: '' }); setError(''); }}
                    className="pharma-btn-secondary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <h3 className="font-display text-xl text-foreground mb-6">Send a Message</h3>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm flex items-start gap-2">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="flex-shrink-0 mt-0.5" aria-hidden="true">
                        <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M9 5.5V9.5M9 12.5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      {error}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <label htmlFor="name" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name *</label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formState.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="form-input"
                        disabled={loading}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="email" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email *</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formState.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="form-input"
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="phone" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Phone</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formState.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX"
                      className="form-input"
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="message" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formState.message}
                      onChange={handleChange}
                      placeholder="How can we help you?"
                      className="form-input resize-none"
                      disabled={loading}
                    />
                  </div>
                  <button
                    type="submit"
                    className="pharma-btn-primary w-full justify-center"
                    disabled={loading}
                    style={{ opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                  >
                    {loading ? (
                      <>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="animate-spin" aria-hidden="true">
                          <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="2" strokeDasharray="22 44" strokeLinecap="round"/>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                          <path d="M3 9H15M10 4l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </>
                    )}
                  </button>
                  <p className="text-xs text-muted-foreground text-center">
                    Your information is kept confidential and will only be used to respond to your enquiry.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}